import {Button, Card, DialogActions, Grid, List, ListItem as ListItemBase, ListItemIcon, ListItemText, Paper, Typography} from "@mui/material";
import React, {useEffect, useState} from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import { Alert } from '@mui/material';
import EnhancedSelect from "../../../../components/EnhancedSelect";
import EnhancedTextField from "../../../../components/EnhancedTextField";
import {Field, Form} from "react-final-form";
import {ChevronRight} from "@mui/icons-material";
import {TaskListCheckIcon} from "../../../../utils/icons";
import {utilAddInventory, utilCheckInventory2, utilUpdateInventory} from "../../../../utils/utils";
import EnhancedCircularLoader from "../../../../components/EnhancedCircularLoader";
import UpdatingDialog from "../../../../components/Dialogs/UpdatingDialog";
import {Link} from "react-router-dom";
import Auth from "../../../../auth";
import { RunRule } from "../../shared/validation";
import type { EdiSpecification, HelpDesk, InventoryEntityRow, Partner, SetInventoryError, ValidationRuleGroup } from "../types";

// Relax third-party / legacy types to `any` (behavior unchanged).
const ListItem: any = ListItemBase;

const includeIfNotEmpty = (value) => value !== "";

const defaultEntity = {
    partner: null,
    supplierSku: "",
    retailerSku: "",
    upc: "",
    manufacturer: "",
    quantity: "",
    unitOfMeasure: "",
    vendorNumber: "",
    status: "",
    cost: "",
    msrp: "",
    retailPrice: "",
    leadTime: "",
    size: "",
    color: "",
    style: "",
    weight: "",
    supplierDescription: "",
    retailerDescription: "",
    nextAvailableQuantity: "",
    nextAvailableDate: "",
    custom1: "",
    custom2: "",
    custom3: "",
    custom4: "",
    custom5: "",
    warehouse: "",
    warehouseQuantity: "",
    additionalWarehouses: Array(24).fill(undefined).map(() => ({
        warehouse: "",
        quantity: "",
        nextAvailableQty: "",
        nextAvailableDate: ""
    }))
}

const fieldGroups = [
    {
        fields: [
            {id: 'supplierSku', label: 'Supplier SKU', gridSize: 3, required: true},
            {id: 'retailerSku', label: 'Retailer SKU', gridSize: 6},
            {id: 'upc', label: 'UPC', gridSize: 3},
            {id: 'manufacturer', label: 'Manufacturer SKU', gridSize: 3}
        ]
    },
    {
        fields: [
            {id: 'quantity', label: 'Quantity', gridSize: 3, required: true, type: 'number', min: 0},
            {id: 'unitOfMeasure', label: 'Unit of Measure', gridSize: 3},
            {id: 'vendorNumber', label: 'Vendor Number', gridSize: 3},
            {id: 'status', label: 'Status', gridSize: 3}
        ]
    },
    {
        fields: [
            {id: 'cost', label: 'Cost', gridSize: 3},
            {id: 'msrp', label: 'MSRP', gridSize: 3},
            {id: 'retailPrice', label: 'Retail Price', gridSize: 3},
            {id: 'leadTime', label: 'Lead Time', gridSize: 3}
        ]
    },
    {
        fields: [
            {id: 'size', label: 'Size', gridSize: 3},
            {id: 'color', label: 'Color', gridSize: 3},
            {id: 'style', label: 'Style', gridSize: 3},
            {id: 'weight', label: 'Weight', gridSize: 3}
        ]
    },
    {
        fields: [
            {id: 'supplierDescription', label: 'Supplier Description', gridSize: 6},
            {id: 'retailerDescription', label: 'Retailer Description', gridSize: 6}
        ]
    },
    {
        fields: [
            {id: 'custom1', label: 'Custom 1', gridSize: 6},
            {id: 'custom2', label: 'Custom 2', gridSize: 6},
        ]
    },
    {
        fields: [
            {id: 'custom3', label: 'Custom 3', gridSize: 6},
            {id: 'custom4', label: 'Custom 4', gridSize: 6}
        ]
    },
    {
        fields: [
            {id: 'custom5', label: 'Custom 5', gridSize: 12}
        ]
    },
    {
        fields: [
            {id: 'nextAvailableQuantity', label: 'Next Available Quantity', gridSize: 6},
            {id: 'nextAvailableDate', label: 'Next Available Date', gridSize: 6}
        ]
    },
    {
        fields: [
            {id: 'warehouse', label: 'Warehouse', gridSize: 6},
            {id: 'warehouseQuantity', label: 'Warehouse Quantity', gridSize: 6}
        ]
    }
];

const warehouseFields = [
    {id: 'warehouse', label: 'Warehouse', gridSize: 3},
    {id: 'quantity', label: 'Quantity', gridSize: 3},
    {id: 'nextAvailableQty', label: 'Next Available Qty', gridSize: 3},
    {id: 'nextAvailableDate', label: 'Next Available Date', gridSize: 3}
];

// Optimized style constants to prevent object recreation on each render
const warehouseRowStyle = {paddingTop: "16px"};
const firstWarehouseRowStyle = {paddingTop: "0"};
const warehouseFieldStyle = {paddingRight: "8px"};
const lastWarehouseFieldStyle = {paddingRight: "0"};
const warehouseContainerStyle = {paddingTop: "16px", width: "100%"};

// Memoized component for individual warehouse rows to prevent unnecessary re-renders
export const WarehouseFieldRow = React.memo(({index, hasInventoryManagePermission}: {index: number; hasInventoryManagePermission: boolean}) => {
    return (
        <Grid
            container
            item
            size={12}
            style={index > 0 ? warehouseRowStyle : firstWarehouseRowStyle}
        >
            {warehouseFields.map((field, fieldIndex) => (
                <Grid
                    key={`${index}-${field.id}`}
                    item
                    size={field.gridSize}
                    style={fieldIndex < warehouseFields.length - 1 ? warehouseFieldStyle : lastWarehouseFieldStyle}
                >
                    <Field
                        name={`additionalWarehouses.${index}.${field.id}`}
                        render={({input, meta}) => {
                            const hasError = meta.touched && meta.error;
                            return (
                                <EnhancedTextField
                                    {...input}
                                    label={field.id === 'warehouse' ? `Warehouse ${index + 2}` : field.label}
                                    error={hasError || undefined}
                                    helperText={hasError ? meta.error : undefined}
                                    disabled={!hasInventoryManagePermission}
                                />
                            );
                        }}
                    />
                </Grid>
            ))}
        </Grid>
    );
});

const fieldMapping = {
    // Form field name (camelCase) to API field name (PascalCase)
    supplierSku: 'SupplierSKU',
    retailerSku: 'MerchantSKU',
    upc: 'UPC',
    manufacturer: 'ManufacturerSKU',
    quantity: 'Quantity',
    unitOfMeasure: 'UOM',
    vendorNumber: 'VendorNumber',
    status: 'Status',
    cost: 'Cost',
    msrp: 'MSRP',
    retailPrice: 'RetailPrice',
    leadTime: 'LeadTime',
    size: 'Size',
    color: 'Color',
    style: 'Style',
    weight: 'Weight',
    supplierDescription: 'SupplierDescription',
    retailerDescription: 'MerchantDescription',
    nextAvailableQuantity: 'NextAvailableQuantity',
    nextAvailableDate: 'NextAvailableDate',
    warehouse: 'Warehouse',
    warehouseQuantity: 'WarehouseQuantity',
    custom1: "Custom1",
    custom2: "Custom2",
    custom3: "Custom3",
    custom4: "Custom4",
    custom5: "Custom5"
};

const reverseFieldMapping = Object.entries(fieldMapping).reduce((acc, [key, value]) => {
    acc[value] = key;
    return acc;
}, {});

const mapFieldToApi = (fieldName) => fieldMapping[fieldName] || fieldName;
const mapApiToForm = (apiFieldName) => reverseFieldMapping[apiFieldName] || apiFieldName;

const isFieldRequired = (fieldId, partnerRules) => {
    // Check if field is unconditionally required in fieldGroups
    if (fieldGroups.find(group => group.fields.find(field => field.id === fieldId)?.required)) return true;

    if (!partnerRules) return false;
    const apiFieldName = mapFieldToApi(fieldId);
    return partnerRules.some(rule =>
        rule.Type === 'required' &&
        (rule.Field === apiFieldName || rule.ExternalField === apiFieldName) &&
        (!rule.Conditions || rule.Conditions.length === 0)
    );
};

export const validate = (values, partnerRules) => {
    const errors: any = {};

    // Always required fields regardless of partner rules
    if (!values.supplierSku) {
        errors.supplierSku = 'Required';
    }
    if (!values.quantity) {
        errors.quantity = 'Required';
    } else if (isNaN(values.quantity) || Number(values.quantity) < 0) {
        errors.quantity = 'Must be a valid non-negative number';
    }

    // If no partner is selected, return basic validation
    if (!values.partner) {
        errors.partner = 'Required';
        return errors;
    }

    // Apply partner-specific rules
    if (partnerRules && partnerRules.length > 0) {
        partnerRules.forEach(rule => {
            // Skip invalid rules
            if (!rule || typeof rule !== 'object' || !rule.Type) {
                return;
            }

            const mappedValues = {};
            Object.keys(values).forEach(key => {
                const apiKey = mapFieldToApi(key);
                if (apiKey) {
                    mappedValues[apiKey] = values[key];
                }
            });

            try {
                // First check if conditions are met
                const conditionsMet = !rule.Conditions || rule.Conditions.every(condition => {
                    const conditionResult = RunRule(mappedValues, condition);
                    return conditionResult.IsValid;
                });

                // Only apply the rule if conditions are met
                if (conditionsMet) {
                    const result = RunRule(mappedValues, rule);
                    if (!result.IsValid) {
                        // Map the API field name back to form field name
                        const apiFieldName = rule.ExternalField || rule.Field;
                        const formFieldName = mapApiToForm(apiFieldName);

                        // Use custom error message if provided, otherwise use the result message
                        const errorMessage = rule.ErrorMessage || result.Message;

                        // Set the error message
                        errors[formFieldName] = errorMessage;
                    }
                }
            } catch (error) {
                console.warn(`Error validating rule for field ${rule.Field}:`, error);
                const apiFieldName = rule.ExternalField || rule.Field;
                const formFieldName = mapApiToForm(apiFieldName);
                errors[formFieldName] = 'Invalid validation rule';
            }
        });
    }

    return errors;
};

interface InventoryEntityProps {
    isRetailer?: boolean;
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    inventoryEntity: InventoryEntityRow | null;
    partners: Partner[];
    setError: SetInventoryError;
    refreshInventory: () => void;
    helpDesks: HelpDesk[];
    ediSpecifications: EdiSpecification[];
    hasInventoryManagePermission: boolean;
    rules: ValidationRuleGroup[];
}

export default function InventoryEntity(props: InventoryEntityProps) {
    const {hasInventoryManagePermission, rules} = props;

    const {open, setOpen, refreshInventory} = props;
    const {inventoryEntity, partners, setError, helpDesks, ediSpecifications} = props;

    const [showAdditionalWarehouses, setShowAdditionalWarehouses] = useState(false);
    const [entity, setEntity] = useState(inventoryEntity || defaultEntity);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const partnerOptions = (partners || []).map((partner: Partner) => ({
        label: partner.CompanyName,
        value: partner.Id
    }));

    const onSubmit = async (values) => {
        // Transform form values back to API structure once at the start
        const partner = values.partner.value;        

        const apiValues = {
            ...(includeIfNotEmpty(values.supplierSku) && { SupplierSKU: values.supplierSku }),
            ...(includeIfNotEmpty(values.retailerSku) && { MerchantSKU: values.retailerSku }),
            ...(includeIfNotEmpty(values.upc) && { UPC: values.upc }),
            ...(includeIfNotEmpty(values.manufacturer) && { ManufacturerSKU: values.manufacturer }),
            ...(includeIfNotEmpty(values.quantity) && { Quantity: values.quantity }),
            ...(includeIfNotEmpty(values.unitOfMeasure) && { UOM: values.unitOfMeasure }),
            ...(includeIfNotEmpty(values.vendorNumber) && { VendorNumber: values.vendorNumber }),
            ...(includeIfNotEmpty(values.status) && { Status: values.status }),
            ...(includeIfNotEmpty(values.cost) && { Cost: values.cost }),
            ...(includeIfNotEmpty(values.msrp) && { MSRP: values.msrp }),
            ...(includeIfNotEmpty(values.retailPrice) && { RetailPrice: values.retailPrice }),
            ...(includeIfNotEmpty(values.leadTime) && { LeadTime: values.leadTime }),
            ...(includeIfNotEmpty(values.size) && { Size: values.size }),
            ...(includeIfNotEmpty(values.color) && { Color: values.color }),
            ...(includeIfNotEmpty(values.style) && { Style: values.style }),
            ...(includeIfNotEmpty(values.weight) && { Weight: values.weight }),
            ...(includeIfNotEmpty(values.supplierDescription) && { SupplierDescription: values.supplierDescription }),
            ...(includeIfNotEmpty(values.nextAvailableQuantity) && { NextAvailableQuantity: values.nextAvailableQuantity }),
            ...(includeIfNotEmpty(values.nextAvailableDate) && { NextAvailableDate: values.nextAvailableDate }),
            ...(includeIfNotEmpty(values.custom1) && { Custom1: values.custom1 }),
            ...(includeIfNotEmpty(values.custom2) && { Custom2: values.custom2 }),
            ...(includeIfNotEmpty(values.custom3) && { Custom3: values.custom3 }),
            ...(includeIfNotEmpty(values.custom4) && { Custom4: values.custom4 }),
            ...(includeIfNotEmpty(values.custom5) && { Custom5: values.custom5 }),
            ...(includeIfNotEmpty(values.warehouse) && { Warehouse: values.warehouse }),
            ...(includeIfNotEmpty(values.warehouseQuantity) && { WarehouseQuantity: values.warehouseQuantity })
        };

        // Add additional warehouses
        values.additionalWarehouses.forEach((warehouse, index) => {
            const warehouseNum = index + 2;
            if (warehouse.warehouse || warehouse.quantity || warehouse.nextAvailableQty || warehouse.nextAvailableDate) {
                // Only add non-empty warehouse fields
                if (warehouse.warehouse !== "") apiValues[`Warehouse${warehouseNum}`] = warehouse.warehouse;
                if (warehouse.quantity !== "") apiValues[`Warehouse${warehouseNum}Quantity`] = warehouse.quantity;
                if (warehouse.nextAvailableQty !== "") apiValues[`Warehouse${warehouseNum}NextAvailableQuantity`] = warehouse.nextAvailableQty;
                if (warehouse.nextAvailableDate !== "") apiValues[`Warehouse${warehouseNum}NextAvailableDate`] = warehouse.nextAvailableDate;
            }
        });

        setSubmitting(true);
        if (inventoryEntity) {
            // Edit
            if (!values.supplierSku) {
                setError({
                    open: true,
                    errorHeader: "Error",
                    errorMessage: "Missing supplier SKU. This item cannot be edited or removed by itself. You will need to clear the feed if you want to remove it."
                });
                return;
            }

            try {
                const response = await utilUpdateInventory(partner, apiValues);
                if (response !== true && response.errorMessage) {
                    setError({
                        open: true,
                        errorHeader: "Error",
                        errorMessage: response.errorMessage
                    });
                } else {
                    await new Promise(resolve => setTimeout(resolve, 750));
                    setOpen(false);
                    refreshInventory();
                }
            } catch (e) {
                setError({
                    open: true,
                    errorHeader: "Error",
                    errorMessage: "An unknown error occurred updating inventory!"
                });
            }
        } else {
            // Add
            try {
                const response = await utilAddInventory(partner, values.retailerSku, values.supplierSku, apiValues);

                if (response !== true && response.errorMessage) {
                    setError({
                        open: true,
                        errorHeader: "Error",
                        errorMessage: response.errorMessage
                    });
                } else {
                    await new Promise(resolve => setTimeout(resolve, 750));
                    setOpen(false);
                    refreshInventory();
                }
            } catch (e) {
                setError({
                    open: true,
                    errorHeader: "Error",
                    errorMessage: "An unknown error occurred adding inventory!"
                });
            }
        }
        setSubmitting(false);
    };

    useEffect(() => {
        const getEntity = async () => {
            setLoading(true);

            const response = await utilCheckInventory2(
                props.isRetailer ? inventoryEntity.supplierId : inventoryEntity.merchantId,
                encodeURIComponent(inventoryEntity.supplierSku)
            );

            if (response.errorStatus) {
                setError({
                    open: true,
                    errorHeader: "Error",
                    errorMessage: response.errorMessage
                });

                setEntity(defaultEntity);
            } else {
                const body = response;
                const partnerId = props.isRetailer ? body.SupplierId : body.MerchantId;

                // Map API response to our form structure
                const mappedEntity = {
                    partner: {
                        label: partnerOptions.find(p => p.value === partnerId)?.label,
                        value: partnerId
                    },
                    supplierSku: body.SupplierSKU,
                    retailerSku: body.MerchantSKU,
                    upc: body.UPC,
                    manufacturer: body.ManufacturerSKU,
                    quantity: body.Quantity === 0 ? "0" : body.Quantity || "",
                    unitOfMeasure: body.UOM,
                    vendorNumber: body.VendorNumber,
                    status: body.Status,
                    cost: body.Cost,
                    msrp: body.MSRP,
                    retailPrice: body.RetailPrice,
                    leadTime: body.LeadTime,
                    size: body.Size,
                    color: body.Color,
                    style: body.Style,
                    weight: body.Weight,
                    supplierDescription: body.SupplierDescription,
                    nextAvailableQuantity: body.NextAvailableQuantity,
                    nextAvailableDate: body.NextAvailableDate,
                    custom1: body.Custom1,
                    custom2: body.Custom2,
                    custom3: body.Custom3,
                    custom4: body.Custom4,
                    custom5: body.Custom5,
                    warehouse: body.Warehouse,
                    warehouseQuantity: body.WarehouseQuantity,
                    // Map additional warehouses (2-25) to array format
                    additionalWarehouses: Array(24).fill(undefined).map((_, i) => {
                        const warehouseNum = i + 2;
                        return {
                            warehouse: body[`Warehouse${warehouseNum}`] || "",
                            quantity: body[`Warehouse${warehouseNum}Quantity`] === 0 ? "0" : body[`Warehouse${warehouseNum}Quantity`] || "",
                            nextAvailableQty: body[`Warehouse${warehouseNum}NextAvailableQuantity`] === 0 ? "0" : body[`Warehouse${warehouseNum}NextAvailableQuantity`] || "",
                            nextAvailableDate: body[`Warehouse${warehouseNum}NextAvailableDate`] || ""
                        };
                    })
                };
                setEntity(mappedEntity);
            }

            setLoading(false);
        }

        if (inventoryEntity) {
            getEntity();
        } else {
            setEntity(defaultEntity);
        }

        // eslint-disable-next-line
    }, [inventoryEntity]);

    useEffect(() => {
        setShowAdditionalWarehouses(false);
    }, [open]);

    return (
        <>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth={"xl"}
                fullWidth={true}
                slotProps={{
                    paper: {
                        style: {
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            overflow: 'hidden',
                            width: '1280px'
                        }
                    }
                }}
            >
                {loading ? (
                    <Paper>
                        <DialogTitle
                            style={{
                                padding: "16px",
                                paddingBottom: "0px"
                            }}
                        >
                            Inventory Information

                            <Alert severity="info" style={{marginTop: "16px"}}>
                                Please note future imports may override changes made on this page.
                            </Alert>
                        </DialogTitle>
                        <DialogContent
                            style={{
                                padding: "16px",
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '400px'
                            }}
                        >
                            <EnhancedCircularLoader/>
                        </DialogContent>
                    </Paper>
                ) : (
                    <Form
                        onSubmit={onSubmit}
                        initialValues={entity}
                        validate={(values) => {
                            let partnerRules = rules.filter(r => r.CoId === (props.isRetailer ? Auth.getAccountNumber() : values.partner?.value));
                            let foundRulesForPartner = partnerRules[0] || {};
                            const errors = validate(values, foundRulesForPartner.Rules);
                            return errors;
                        }}
                        validateOnBlur={true}
                        subscription={{
                            values: true,
                            submitting: true,
                            pristine: true,
                            invalid: true,
                            touched: true,
                            errors: true
                        }}
                        render={({handleSubmit, submitting, pristine, invalid, values, errors}) => {
                            let hasEdiSpecifications = ediSpecifications.length > 0 && ediSpecifications.find(spec => spec.PartnerCoId === values.partner?.value);
                            let ediSpecLink = hasEdiSpecifications ? ediSpecifications.find(spec => spec.PartnerCoId === values.partner?.value).SpecLink : null;
                            let selectedRules = (rules.filter(r => r.CoId === (props.isRetailer ? Auth.getAccountNumber() : values.partner?.value))[0] || []) as ValidationRuleGroup;
                            let partnerRules = selectedRules.Rules || [];

                            return (
                                <form onSubmit={handleSubmit}>
                                    <Grid container spacing={3}>
                                        {/* Left Panel - Inventory Form */}
                                        <Grid item size={values.partner ? 8 : 12}>
                                            <Paper style={{borderRadius: '4px'}}>
                                                <DialogTitle
                                                    style={{
                                                        padding: "16px",
                                                        paddingBottom: "0px"
                                                    }}
                                                >
                                                    {inventoryEntity ? (
                                                        <>
                                                            {inventoryEntity.supplierSku}
                                                        </>
                                                    ) : (
                                                        <>Inventory Information</>
                                                    )}

                                                    <Alert severity="info" style={{marginBottom: "16px"}}>
                                                        Please note future imports may override changes made on this
                                                        page.
                                                    </Alert>
                                                </DialogTitle>

                                                <DialogContent
                                                    style={{
                                                        padding: "16px",
                                                        paddingTop: "0px"
                                                    }}
                                                >
                                                    <Grid container style={{paddingTop: "16px"}}>
                                                        <Grid item size={12}>
                                                            <Field
                                                                name="partner"
                                                                render={({input, meta}) => (
                                                                    <div>
                                                                        <EnhancedSelect
                                                                            value={input.value}
                                                                            onChange={input.onChange}
                                                                            onBlur={input.onBlur}
                                                                            label="Partner"
                                                                            options={partnerOptions}
                                                                            isClearable
                                                                            disabled={!hasInventoryManagePermission || inventoryEntity}
                                                                        />
                                                                        {(!input.value || (meta.touched && meta.error)) && (
                                                                            <div style={{
                                                                                color: 'red',
                                                                                fontSize: '0.75rem',
                                                                                marginTop: '3px'
                                                                            }}>
                                                                                {!input.value ? 'Select a partner to continue' : meta.error}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            />
                                                        </Grid>

                                                        {fieldGroups.map((group, groupIndex) => {
                                                            return (
                                                                <Grid
                                                                    key={groupIndex}
                                                                    container
                                                                    item
                                                                    size={12}
                                                                    style={{paddingTop: "16px"}}
                                                                >
                                                                    {group.fields.map((field, fieldIndex) => {
                                                                        if(inventoryEntity && field.id === "supplierSku") return <></>;
                                                                        return (
                                                                            <Grid
                                                                                key={field.id}
                                                                                item
                                                                                size={field.gridSize}
                                                                                style={{
                                                                                    paddingRight: fieldIndex < group.fields.length - 1 ? "8px" : "0"
                                                                                }}
                                                                            >
                                                                                <Field
                                                                                    name={field.id}
                                                                                    render={({input, meta}) => {
                                                                                        const isRequired = isFieldRequired(field.id, partnerRules);
                                                                                        const hasError = (errors && errors[field.id]) || (meta.error && (meta.touched || meta.submitFailed));
                                                                                        const errorMessage = (errors && errors[field.id]) || (hasError && meta.error);
                                                                                        
                                                                                        return (
                                                                                            <div style={{ position: 'relative', width: '100%' }}>
                                                                                                <EnhancedTextField
                                                                                                    {...input}
                                                                                                    label={field.label}
                                                                                                    required={isRequired}
                                                                                                    error={hasError}
                                                                                                    helperText={errorMessage || ''}
                                                                                                    disabled={!hasInventoryManagePermission}
                                                                                                    onBlur={input.onBlur}
                                                                                                    type={field.type || 'text'}
                                                                                                    inputProps={field.type === 'number' ? { min: field.min || 0 } : undefined}
                                                                                                />
                                                                                            </div>
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Grid>
                                                                        );
                                                                    })}
                                                                </Grid>
                                                            );
                                                        })}

                                                        <div style={{
                                                            display: 'flex',
                                                            flexDirection: 'column-reverse',
                                                            width: '100%'
                                                        }}>
                                                            <Grid container style={{paddingTop: "24px"}}>
                                                                <Grid item size={6}>
                                                                    <Button
                                                                        variant="outlined"
                                                                        color="primary"
                                                                        onClick={() => setShowAdditionalWarehouses(!showAdditionalWarehouses)}
                                                                        style={{
                                                                            width: "100%",
                                                                            padding: "12px",
                                                                            justifyContent: "center"
                                                                        }}
                                                                        disabled={!hasInventoryManagePermission}
                                                                    >
                                                                        {showAdditionalWarehouses ? "Hide" : "Show"} Additional
                                                                        Warehouses
                                                                    </Button>
                                                                </Grid>
                                                            </Grid>

                                                            {showAdditionalWarehouses && (
                                                                <div style={warehouseContainerStyle}>
                                                                    {values.additionalWarehouses.map((_, index) => (
                                                                        <WarehouseFieldRow
                                                                            key={`warehouse-${index}`}
                                                                            index={index}
                                                                            hasInventoryManagePermission={hasInventoryManagePermission}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Grid>

                                                    <DialogActions>
                                                        <Button onClick={() => setOpen(false)} variant="text">
                                                            Cancel
                                                        </Button>

                                                        {hasInventoryManagePermission && (
                                                            <Button
                                                                type="submit"
                                                                variant="text"
                                                                color="primary"
                                                                disabled={submitting || pristine || invalid || !hasInventoryManagePermission}
                                                            >
                                                                {inventoryEntity ? "Update" : "Add"}
                                                            </Button>
                                                        )}
                                                    </DialogActions>
                                                </DialogContent>
                                            </Paper>
                                        </Grid>

                                        {/* Right Panel - Specifications */}
                                        {values.partner && (
                                            <Grid item size={4}>
                                                <Card style={{borderRadius: '4px'}}>
                                                    <Typography variant="h6" style={{
                                                        padding: "16px",
                                                        borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                                                    }}>
                                                        Specifications
                                                        for {partnerOptions.find(p => p.value === values.partner)?.label}
                                                    </Typography>
                                                    <List>
                                                        <ListItem button
                                                                  component={Link}
                                                                  to={`/document-standards?type=Inventory&partner=${values.partner.value}`}
                                                                  target="_blank"
                                                                  rel="noopener noreferrer"
                                                                  style={{color: 'rgba(0, 0, 0, 0.87)'}}
                                                        >
                                                            <ListItemIcon style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}>
                                                                <TaskListCheckIcon width="30" height="30"/>
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary="Review Retailer's Requirements"
                                                                secondary="See the retailer's required and optional fields"
                                                            />
                                                            <ListItemIcon style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}>
                                                                <ChevronRight
                                                                    style={{width: '40px', height: '40px'}}/>
                                                            </ListItemIcon>
                                                        </ListItem>
                                                        {
                                                            hasEdiSpecifications && ediSpecLink && (
                                                                <ListItem button
                                                                          component='a'
                                                                          href={ediSpecLink}
                                                                          target='_blank'
                                                                          rel="noopener noreferrer"
                                                                          style={{color: 'rgba(0, 0, 0, 0.87)'}}
                                                                >
                                                                    <ListItemIcon style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center'
                                                                    }}>
                                                                        <TaskListCheckIcon width="30" height="30"/>
                                                                    </ListItemIcon>
                                                                    <ListItemText
                                                                        primary="EDI Specifications"
                                                                        secondary="See the retailer's EDI specifications"
                                                                    />
                                                                    <ListItemIcon style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center'
                                                                    }}>
                                                                        <ChevronRight
                                                                            style={{width: '40px', height: '40px'}}/>
                                                                    </ListItemIcon>
                                                                </ListItem>
                                                            )
                                                        }
                                                        <ListItem button
                                                                  component='a'
                                                                  target='_blank'
                                                                  href={helpDesks.length > 0
                                                                      ? `https://${helpDesks[0].Domain}.logicbroker.com/hc/en-us`
                                                                      : 'https://help.logicbroker.com/hc/en-us'}
                                                                  style={{color: 'rgba(0, 0, 0, 0.87)'}}
                                                        >
                                                            <ListItemIcon style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}>
                                                                <TaskListCheckIcon width="30" height="30"/>
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary="Knowledge Base"
                                                                secondary="See platform documentation and browse articles"
                                                            />
                                                            <ListItemIcon style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}>
                                                                <ChevronRight
                                                                    style={{width: '40px', height: '40px'}}/>
                                                            </ListItemIcon>
                                                        </ListItem>
                                                    </List>
                                                </Card>
                                            </Grid>
                                        )}
                                    </Grid>
                                </form>
                            );
                        }}
                    />
                )}
            </Dialog>
            <UpdatingDialog
                open={submitting}
                setOpen={setSubmitting}
                title={"Updating Inventory..."}
                subtitle={"Please wait while we update the inventory..."}
            />
        </>
    );
}
